import { useMemo, useState } from 'react';

import type { ZoneMetrics } from '../../../shared/dashboard-data';
import { formatArea, formatLengthKm } from '../../utils/formatters';
import type { MetricTab } from '../../utils/types';

interface ZoneDataTabsProps {
  zones: ZoneMetrics[];
}

interface MetricRow {
  zoneName: string;
  displayName: string;
  isReal: boolean;
  value: number;
  width: number;
}

export function ZoneDataTabs({ zones }: ZoneDataTabsProps) {
  const [metricTab, setMetricTab] = useState<MetricTab>('area');

  const rows = useMemo<MetricRow[]>(() => {
    const sorted = [...zones].sort((a, b) => a.order - b.order || a.zoneName.localeCompare(b.zoneName, 'zh-CN'));

    const maxValue =
      metricTab === 'area'
        ? Math.max(...sorted.map((zone) => zone.areaM2), 1)
        : Math.max(...sorted.map((zone) => zone.shapeLengthKm), 1);

    return sorted.map((zone) => {
      const value = metricTab === 'area' ? zone.areaM2 : zone.shapeLengthKm;
      return {
        zoneName: zone.zoneName,
        displayName: zone.displayName,
        isReal: !zone.isMock,
        value,
        width: Math.max((value / maxValue) * 100, 6),
      };
    });
  }, [metricTab, zones]);

  return (
    <div className="tabs-panel">
      <div className="panel-title-row">
        <h2 className="panel-title panel-title-inline">区域绿化统计</h2>
        <div className="tab-header">
          <button
            type="button"
            className={`tab-btn ${metricTab === 'area' ? 'active' : ''}`}
            onClick={() => setMetricTab('area')}
          >
            面积
          </button>
          <button
            type="button"
            className={`tab-btn ${metricTab === 'length' ? 'active' : ''}`}
            onClick={() => setMetricTab('length')}
          >
            长度
          </button>
        </div>
      </div>

      <ul className="bar-list">
        {rows.map((row) => (
          <li key={row.zoneName} className={`bar-row ${row.isReal ? 'is-real' : ''}`}>
            <span className="bar-label">{row.displayName}</span>
            <div className="bar-track">
              <span className="bar-fill" style={{ width: `${row.width}%` }} />
            </div>
            <span className="bar-value">
              {metricTab === 'area' ? `${formatArea(row.value)} ㎡` : `${formatLengthKm(row.value)} km`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
